import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { TimePicker } from "@/components/TimePicker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DateTimePickerProps = {
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  showTime?: boolean;
};

export function DateTimePicker({
  value,
  onChange,
  disabled = false,
  placeholder = "Select date and time",
  className,
  showTime = true,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : undefined
  );
  const [timeValue, setTimeValue] = useState<string>(
    value ? format(new Date(value), "HH:mm") : ""
  );

  // Sync internal state with value prop changes
  useEffect(() => {
    if (value) {
      try {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          setSelectedDate(date);
          setTimeValue(format(date, "HH:mm"));
        } else {
          setSelectedDate(undefined);
          setTimeValue("");
        }
      } catch {
        setSelectedDate(undefined);
        setTimeValue("");
      }
    } else {
      setSelectedDate(undefined);
      setTimeValue("");
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date && timeValue) {
      try {
        const dateTime = new Date(date);
        const [hours, minutes] = timeValue.split(":").map(Number);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          dateTime.setHours(hours, minutes);
          onChange(dateTime.toISOString());
        }
      } catch {
        // Ignore invalid date creation
      }
    }
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    if (selectedDate && time) {
      try {
        const dateTime = new Date(selectedDate);
        const [hours, minutes] = time.split(":").map(Number);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          dateTime.setHours(hours, minutes);
          onChange(dateTime.toISOString());
        }
      } catch {
        // Ignore invalid date creation
      }
    }
  };

  const displayValue = selectedDate
    ? showTime && timeValue
      ? `${format(selectedDate, "MMM d, yyyy")} at ${timeValue}`
      : format(selectedDate, "MMM d, yyyy")
    : placeholder;

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full rounded-md justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 size-4" aria-hidden="true" />
            {displayValue}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={4}
          className="w-auto p-0"
        >
          <div className="p-3">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
            {showTime && (
              <div className="mt-3 border-t pt-3">
                <Label htmlFor="time-input" className="text-sm font-medium">
                  Time
                </Label>
                <TimePicker
                  id="time-input"
                  value={timeValue}
                  onChange={handleTimeChange}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateTimePicker;
